import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Alert,
  Button,
} from "@mui/material";
import apiService from "../../../services/api";
import useCodeCommon from "../../../hooks/useCodeCommon";
import useHubs from "../../../hooks/useHubs";
import ImagePreviewCard from "../../../components/common/ImagePreviewCard";
import DefaultImage from "../../../assets/images/DefaultImage.png";
import CustomLoading from "../../../components/common/CustomLoading";

interface VehicleModelCode {
  codeid: number;
  codevalue: string;
  codedisplayname: string;
  categoryid: number;
  codediscription: string;
  categoryname: string;
  displayorder: number;
}

const BASE_IMAGE_URL = `${(process.env.REACT_APP_BASE_IMAGE_URL || "").replace(/\/?$/, '/')}`;

const VehicleManagementDetails: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [vehicleData, setVehicleData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageModal, setImageModal] = useState<{ open: boolean; src: string; title: string }>({ open: false, src: '', title: '' });
  const [vehicleModelCodes, setVehicleModelCodes] = useState<VehicleModelCode[]>([]);
  const [vehicleModelLoading, setVehicleModelLoading] = useState(false);

  const { options: allStatusOptions, loading: statusLoading, error: statusError } = useCodeCommon('VehicleStatus');
  const { options: vehicleTypeOptions } = useCodeCommon("VehicleType");
  const { options: evSpeedTypeOptions } = useCodeCommon("EVSpeedType");
  const { hubOptions } = useHubs(setError);
  
  // Use all status options instead of filtering to support all status types
  const statusOptions = allStatusOptions;

  const getVehicleModelDisplayName = (codeValue: string | number | null | undefined): string => {
    if (codeValue === null || codeValue === undefined) return '';
    const code = vehicleModelCodes.find(c => c.codevalue === String(codeValue));
    return code?.codedisplayname || '';
  };

  const getVehicleModelNameByCodeValue = (vehiclemodelid: number | string | null | undefined): string => {
    if (vehiclemodelid === null || vehiclemodelid === undefined) return 'N/A';
    return getVehicleModelDisplayName(vehiclemodelid) || 'N/A';
  };

  // Fetch vehicle models on component mount
  useEffect(() => {
    const fetchVehicleModels = async () => {
      try {
        setVehicleModelLoading(true);
        const response: any = await apiService.getAllCodeCommonByCategory("VehicleModel");
        const raw = (response as any)?.data || response || [];
        const codes = Array.isArray(raw) ? raw : [];
        // Sort by displayorder
        const sortedCodes = [...codes].sort((a: VehicleModelCode, b: VehicleModelCode) => 
          a.displayorder - b.displayorder
        );
        setVehicleModelCodes(sortedCodes);
      } catch (err: any) {
        console.error('Error fetching VehicleModel codes:', err);
        setVehicleModelCodes([]);
      } finally {
        setVehicleModelLoading(false);
      }
    };

    fetchVehicleModels();
  }, []);

  // Fetch vehicle details after vehicle model codes are loaded
  useEffect(() => {
    if (id && vehicleModelCodes.length > 0 && !vehicleModelLoading) {
      fetchVehicleDetails(id);
    }
  }, [id, vehicleModelCodes.length, vehicleModelLoading]);

  const parseVehicleImages = (raw: any): string[] => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw as string[];
    let value: any = raw;
    for (let i = 0; i < 3; i += 1) {
      if (typeof value === "string") {
        try {
          value = JSON.parse(value);
        } catch {
          break;
        }
      }
    }
    if (Array.isArray(value)) {
      const flattened = value.flat?.(3) ?? value;
      return flattened.filter((x: any) => typeof x === "string");
    }
    return [];
  };

  const categorizeImages = (images: string[]) => {
    const vehicleImages = images.filter(img => img.includes('/Vehicle_Images/') || img.includes('/vehicle/'));
    const batteryImages = images.filter(img => img.includes('/Battery_Images/') || img.includes('/battery/'));
    const chargerImages = images.filter(img => img.includes('/Charger_Images/') || img.includes('/charger/'));
    
    return {
      vehicleImages,
      batteryImages,
      chargerImages
    };
  };

  const resolvePreviewSrc = (value: string): string => {
    try {
      const v = String(value || "").trim();
      if (!v) return `${BASE_IMAGE_URL}${DefaultImage}`;
      if (v.startsWith("[")) {
        const arr = JSON.parse(v);
        if (Array.isArray(arr) && arr.length > 0) {
          const first = String(arr[0] || "");
          if (!first) return `${BASE_IMAGE_URL}${DefaultImage}`;
          if (first.startsWith('http')) return first;
          const cleaned = first.replace(/^\/+/, '');
          return `${BASE_IMAGE_URL}${cleaned}`;
        }
      }
      if (v.startsWith('http')) return v;
      const cleaned = v.replace(/^\/+/, '');
      return `${BASE_IMAGE_URL}${cleaned || DefaultImage}`;
    } catch {
      return `${BASE_IMAGE_URL}${DefaultImage}`;
    }
  };

  const getVehicleStatusText = (status: any) => {
    const statusOption = statusOptions.find(option => option.codevalue === String(status));
    return statusOption ? statusOption.codedisplayname : String(status) || "Active";
  };


  const getVehicleStatusStyles = (status: any) => {
    const text = getVehicleStatusText(status);
    const styles: Record<string, { bg: string; color: string }> = {
      Active: { bg: "rgba(212, 255, 199, 1)", color: "rgba(31, 113, 13, 1)" },
      Banned: { bg: "rgba(255, 227, 227, 1)", color: "rgba(197, 0, 0, 1)" },
      "Under Maintenance": { bg: "rgba(215, 215, 215, 1)", color: "rgba(60, 60, 60, 1)" },
      Booked: { bg: "rgba(255, 245, 157, 1)", color: "rgba(107, 105, 14, 1)" },
      Available: { bg: "rgba(212, 255, 199, 1)", color: "rgba(31, 113, 13, 1)" },
      B2B: { bg: "rgba(237, 231, 255, 1)", color: "rgba(81, 45, 168, 1)" },
      Inactive: { bg: "rgba(255, 236, 209, 1)", color: "rgba(230, 81, 0, 1)" },
    };
    return styles[text] || { bg: "#eee", color: "#666" };
  };

  const getHubName = (hubId: any) => {
    if (!hubId || !hubOptions || hubOptions.length === 0) return "N/A";
    const hub = hubOptions.find(h => h.hubmasterid === hubId || String(h.hubmasterid) === String(hubId));
    return hub ? hub.hubname : "N/A";
  };

  const getVehicleTypeText = (vehicleType: any) => {
    if (!vehicleType || !vehicleTypeOptions || vehicleTypeOptions.length === 0) return "N/A";
    const typeOption = vehicleTypeOptions.find(option => option.codevalue === String(vehicleType));
    return typeOption ? typeOption.codedisplayname : "N/A";
  };

  const getSpeedTypeText = (speedType: any) => {
    if (!speedType || !evSpeedTypeOptions || evSpeedTypeOptions.length === 0) return "N/A";
    const speedOption = evSpeedTypeOptions.find(option => option.codevalue === String(speedType));
    return speedOption ? speedOption.codedisplayname : "N/A";
  };

  const isHighSpeed = (speedType: any) => {
    const speedTypeText = getSpeedTypeText(speedType);
    return speedTypeText && speedTypeText.toLowerCase().includes("high");
  };

  const fetchVehicleDetails = async (vehicleId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getVehicleById(vehicleId);
      const d = (response as any)?.data || response || {};

      // Get model display name - vehicleModelCodes should be loaded by now
      const vehiclemodelid = d.vehiclemodelid || d.vehiclemodel || null;
      const modelName = getVehicleModelNameByCodeValue(vehiclemodelid);

      const normalized = {
        vehicleid: d.vehicleid || "N/A",
        vehiclename: d.vehiclename || "N/A",
        vehiclemodel: d.vehiclemodel || "N/A",
        vehiclemodelid: vehiclemodelid,
        model: modelName,
        registrationnumber: d.registrationnumber || "N/A",
        rangeperchargekm: d.rangeperchargekm || "N/A",
        topspeed: d.topspeed || "N/A",
        weight: d.weight || "N/A",
        telematicsdeviceid: d.telematicsdeviceid ||  "N/A",
        hubmasterid: d.hubmasterid || "N/A",
        status: d.status,
        speedtype: d.speedtype,
        vehicletype: d.vehicletype,
        vinnumber: d.vinnumber || d.vinNumber || "",
        vehiclenum: d.vehiclenum || d.vehiclenumber || d.vehicleNumber || "",
        motorid: d.motorid || d.vcunum || d.vcunumber || d.vcuNumber || "",
        otherspecifications: d.otherspecifications || "N/A",
        vehicleimages: parseVehicleImages(d.vehicleimages),
        batteryno1: d.batteryno1 || "N/A",
        batteryno2: d.batteryno2 || "N/A",
        chargeno: d.chargeno || "N/A",
      };

      setVehicleData(normalized);
    } catch (error: any) {
      setError("Failed to load vehicle details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

 

  const getStatusText = (status: any) => getVehicleStatusText(status);

  if (loading || vehicleModelLoading) {
    return <CustomLoading message="Fetching Vehicle Details..." />;
  }

  if (error) {
    return (
      <Container fluid>
        <Alert severity="error" className="mb-3">
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => navigate("/vehicle-management", { state: location.state })}>
          Go Back
        </Button>
      </Container>
    );
  }

  if (!vehicleData) {
    return (
      <Container fluid>
        <Alert severity="warning" className="mb-3">
          No vehicle data found.
        </Alert>
        <Button variant="outlined" onClick={() => navigate("/vehicle-management", { state: location.state })}>
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="pb-5">
      <Paper className="mb-4 shadow-sm border-0 rounded-3">
        <Box className="px-4 pt-4 pb-4">
          <Box className="d-flex justify-content-between align-items-center mb-3">
            <Typography
              variant="h6"
              className="fw-bold mb-0"
              sx={{ color: "rgba(13,44,90,1)", fontSize: "1.1rem" }}
            >
              Basic Information
            </Typography>
            {(() => {
              const s = getVehicleStatusStyles(vehicleData.status);
              return (
                <Box
                  sx={{
                    px: 2,
                    py: 0.5,
                    borderRadius: 2,
                    backgroundColor: s.bg,
                    color: s.color,
                    fontSize: "0.8rem",
                    fontWeight: "bold",
                  }}
                >
                  {getStatusText(vehicleData.status)}
                </Box>
              );
            })()}
          </Box>

          <Box
            sx={{
              height: "1px",
              backgroundColor: "#e9ecef",
              mb: 3,
            }}
          />

          <Row className="g-4">
              {[
              { label: "Vehicle Name", value: vehicleData.vehiclename || "N/A" },
              { label: "Model", value: vehicleData.model || "N/A" },
              { label: "Range (km/charge)", value: vehicleData.rangeperchargekm || "N/A" },
              { label: "Top Speed (kmph)", value: vehicleData.topspeed || "N/A" },
              { label: "Weight", value: vehicleData.weight || "N/A" },
            ].map((field: { label: string; value: any }, idx: number) => (
              <Col xs={12} sm={6} md={4} key={idx}>
                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    variant="body2"
                    sx={{ 
                      color: "#6c757d", 
                      mb: 0.5,
                      fontSize: "0.85rem",
                      fontWeight: 500
                    }}
                  >
                    {field.label}
                  </Typography>
                  <Typography
                    variant="body1"
                    className="fw-bold"
                    sx={{ 
                      color: "#495057",
                      fontSize: "0.95rem",
                      wordBreak: "break-word"
                    }}
                  >
                    {field.value}
                  </Typography>
                </Box>
              </Col>
            ))}
          </Row>
        </Box>
      </Paper>

      <Paper className="mb-4 shadow-sm border-0 rounded-3">
        <Box className="px-4 pt-4 pb-4">
          <Typography
            variant="h6"
            className="fw-bold mb-3"
            sx={{ color: "rgba(13,44,90,1)", fontSize: "1.1rem" }}
          >
            Vehicle Specifications
          </Typography>

          <Box
            sx={{
              height: "1px",
              backgroundColor: "#e9ecef",
              mb: 3,
            }}
          />

          <Row className="g-4">
              {[
              { label: "Battery No 1", value: vehicleData.batteryno1 || "N/A" },
              { label: "Battery No 2", value: vehicleData.batteryno2 || "N/A" },
              { label: "Charge No.", value: vehicleData.chargeno || "N/A" },
            ].map((field: { label: string; value: any }, idx: number) => (
              <Col xs={12} sm={6} md={4} key={idx}>
                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    variant="body2"
                    sx={{ 
                      color: "#6c757d", 
                      mb: 0.5,
                      fontSize: "0.85rem",
                      fontWeight: 500
                    }}
                  >
                    {field.label}
                  </Typography>
                  <Typography
                    variant="body1"
                    className="fw-bold"
                    sx={{ 
                      color: "#495057",
                      fontSize: "0.95rem",
                      wordBreak: "break-word"
                    }}
                  >
                    {field.value}
                  </Typography>
                </Box>
              </Col>
            ))}
          </Row>
        </Box>
      </Paper>

      <Paper className="mb-4 shadow-sm border-0 rounded-3">
        <Box className="px-4 pt-4 pb-4">
          <Typography
            variant="h6"
            className="fw-bold mb-3"
            sx={{ color: "rgba(13,44,90,1)", fontSize: "1.1rem" }}
          >
            Registration & Connection
          </Typography>

          <Box
            sx={{
              height: "1px",
              backgroundColor: "#e9ecef",
              mb: 3,
            }}
          />

          <Row className="g-4">
              {[
              { label: "Registration No.", value: vehicleData.registrationnumber || "N/A" },
              { label: "Vehicle Number", value: vehicleData.vehiclenum || "N/A" },
              { label: "Telematics Device ID", value: vehicleData.telematicsdeviceid || "N/A" },
              { label: "Assign Hub", value: getHubName(vehicleData.hubmasterid) },
              { label: "Status", value: getStatusText(vehicleData.status) },
              { label: "Vehicle Type", value: getVehicleTypeText(vehicleData.vehicletype) },
              { label: "Speed Type", value: getSpeedTypeText(vehicleData.speedtype) },
              ...(isHighSpeed(vehicleData.speedtype) ? [{ label: "VIN Number", value: vehicleData.vinnumber || "N/A" }] : []),
              { label: "Motor ID", value: vehicleData.motorid || "N/A" },
            ].map((field: { label: string; value: any }, idx: number) => (
              <Col xs={12} sm={6} md={4} key={idx}>
                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    variant="body2"
                    sx={{ 
                      color: "#6c757d", 
                      mb: 0.5,
                      fontSize: "0.85rem",
                      fontWeight: 500
                    }}
                  >
                    {field.label}
                  </Typography>
                  <Typography
                    variant="body1"
                    className="fw-bold"
                    sx={{ 
                      color: "#495057",
                      fontSize: "0.95rem",
                      wordBreak: "break-word"
                    }}
                  >
                    {field.value}
                  </Typography>
                </Box>
              </Col>
            ))}
          </Row>
        </Box>
      </Paper>

      {vehicleData.otherspecifications && (
        <Paper className="mb-4 shadow-sm border-0 rounded-3">
          <Box className="px-4 pt-4 pb-4">
            <Typography
              variant="h6"
              className="fw-bold mb-3"
              sx={{ color: "rgba(13,44,90,1)", fontSize: "1.1rem" }}
            >
              Other Specifications
            </Typography>

            <Box
              sx={{
                height: "1px",
                backgroundColor: "#e9ecef",
                mb: 3,
              }}
            />

            <Box sx={{ textAlign: "left" }}>
              <Typography
                variant="body1"
                sx={{ 
                  color: "#495057",
                  fontSize: "0.95rem",
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap"
                }}
              >
                {vehicleData.otherspecifications || "N/A"}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      <Paper className="mb-4 shadow-sm border-0 rounded-3">
        <Box className="px-4 pt-4 pb-4">
          <Typography
            variant="h6"
            className="fw-bold mb-3"
            sx={{ color: "rgba(13,44,90,1)", fontSize: "1.1rem" }}
          >
            Attachments
          </Typography>

          <Box
            sx={{
              height: "1px",
              backgroundColor: "#e9ecef",
              mb: 3,
            }}
          />

          <Row className="g-3">
            {(() => {
              const allImages = parseVehicleImages(vehicleData.vehicleimages);
              const { vehicleImages, batteryImages, chargerImages } = categorizeImages(allImages);
              const hasAnyImages = vehicleImages.length > 0 || batteryImages.length > 0 || chargerImages.length > 0;
              
              if (!hasAnyImages) {
                return (
                  <Col xs={12}>
                    <Box
                      sx={{
                        p: 4,
                        textAlign: "center",
                        backgroundColor: "#f8f9fa",
                        borderRadius: 2,
                        border: "2px dashed #dee2e6",
                      }}
                    >
                      <Typography variant="body1" sx={{ color: "#6c757d" }}>
                        No attachments available
                      </Typography>
                    </Box>
                  </Col>
                );
              }

              return (
                <>
                  {vehicleImages.length > 0 && (
                    <Col xs={12}>
                      <Typography variant="body2" color="textSecondary" className="mb-2 fw-bold">
                        Vehicle Images
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                        {vehicleImages.map((img: string, idx: number) => (
                          <ImagePreviewCard
                            key={`vehicle-${img}-${idx}`}
                            title={`Vehicle Image ${idx + 1}`}
                            value={img}
                            resolveSrc={resolvePreviewSrc}
                            size={200}
                            defaultImage={DefaultImage}
                          />
                        ))}
                      </Box>
                    </Col>
                  )}

                  {batteryImages.length > 0 && (
                    <Col xs={12}>
                      <Typography variant="body2" color="textSecondary" className="mb-2 fw-bold">
                        Battery Images
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                        {batteryImages.map((img: string, idx: number) => (
                          <ImagePreviewCard
                            key={`battery-${img}-${idx}`}
                            title={`Battery Image ${idx + 1}`}
                            value={img}
                            resolveSrc={resolvePreviewSrc}
                            size={200}
                            defaultImage={DefaultImage}
                          />
                        ))}
                      </Box>
                    </Col>
                  )}

                  {chargerImages.length > 0 && (
                    <Col xs={12}>
                      <Typography variant="body2" color="textSecondary" className="mb-2 fw-bold">
                        Charger Images
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                        {chargerImages.map((img: string, idx: number) => (
                          <ImagePreviewCard
                            key={`charger-${img}-${idx}`}
                            title={`Charger Image ${idx + 1}`}
                            value={img}
                            resolveSrc={resolvePreviewSrc}
                            size={200}
                            defaultImage={DefaultImage}
                          />
                        ))}
                      </Box>
                    </Col>
                  )}
                </>
              );
            })()}
          </Row>
        </Box>
      </Paper>

      <Paper
        className="mt-4 shadow-sm border-0 rounded-3"
        sx={{
          position: { xs: "fixed", md: "static" },
          bottom: { xs: 0, md: "auto" },
          left: 0,
          right: 0,
          zIndex: 1000,
        }}
      >
      </Paper>
    </Container>
  );
};


export default VehicleManagementDetails;
